// B2 — Tenant detayı (mockup panel/PagesOperate.jsx TenantDetailPage
// portu). GET /ops/v1/tenants/{id}. Mockup'taki kullanım / faturalama /
// destek / entegrasyon / tehlikeli bölge sekmeleri + kişiler + olaylar +
// MRR'ın backend karşılığı yok, çizilmez. Denetim sekmesi yalnız OPERATÖR
// denetim havuzunu (GET /ops/v1/audit?target_tenant_id) gösterir: müşterinin
// kendi denetim kaydını operatöre açan bir uç yok.
//
// RBAC: tenants.read — liste sayfasıyla aynı gate (backend her okumayı
// operator.tenant_detail_read denetim satırı olarak yazar).

import { useState } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ServerCog, UserCog } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useParams } from "react-router";

import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  DataTable,
  Empty,
  ErrorState,
  Loading,
  PageHeader,
  Tab,
  TabList,
  TabPanel,
  Tabs,
  type BadgeTone,
  type DataTableColumn,
} from "@novemai/components";

import { unwrap } from "@/api/envelope";
import { operatorGraph } from "@/api/operator-graph";
import type { paths } from "@/api/types.gen";
import { AuditEventsTable } from "@/components/AuditEventsTable";
import { CursorPager, useCursorStack } from "@/components/CursorPager";
import { ProvisionTenantModal } from "@/components/ProvisionTenantModal";
import { RequireAction } from "@/components/RequireRole";
import { StartImpersonationModal } from "@/components/StartImpersonationModal";
import { TenantMark } from "@/components/TenantMark";
import { usePermission } from "@/hooks/usePermission";
import { resolveErrorDisplay } from "@/lib/envelope-error-display";
import { cn } from "@/lib/utils";

type TenantDetailData = NonNullable<
  paths["/ops/v1/tenants/{id}"]["get"]["responses"][200]["content"]["application/json"]["data"]
>;

type TenantEmployeeData = NonNullable<
  paths["/ops/v1/tenants/{id}/employees"]["get"]["responses"][200]["content"]["application/json"]["data"]
>;

type TenantEmployee = TenantEmployeeData["employees"][number];

const STATE_TONE: Readonly<Record<string, BadgeTone>> = {
  active: "success",
  trial: "info",
  suspended: "danger",
  offboarding: "warning",
};

async function fetchTenant(id: string): Promise<TenantDetailData> {
  return unwrap(operatorGraph.GET("/ops/v1/tenants/{id}", { params: { path: { id } } }));
}

async function fetchTenantEmployees(id: string): Promise<TenantEmployeeData> {
  return unwrap(
    operatorGraph.GET("/ops/v1/tenants/{id}/employees", { params: { path: { id } } }),
  );
}

/**
 * Firmanın çalışan dizini. Ayrı bir sorgu (tenant detayına gömülmedi): her
 * çağrı backend'de bir operator.tenant_employee_directory_read denetim satırı
 * yazıyor ve bunun kimlik şeridinin yeniden çizilmesine bağlanması, denetim
 * kaydını gürültüye boğardı.
 */
function TenantEmployeesCard({ tenantId }: { readonly tenantId: string }): React.ReactElement {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ["operator", "tenant-employees", tenantId],
    queryFn: () => fetchTenantEmployees(tenantId),
    staleTime: 60_000,
  });

  const columns: readonly DataTableColumn<TenantEmployee>[] = [
    {
      id: "name",
      header: t("tenant_detail.employees_col_name"),
      cell: (row) => (
        <div>
          <div className="font-sans text-[13px] text-[var(--fg-primary)]">{row.display_name}</div>
          <div className="font-mono text-[11.5px] text-[var(--fg-muted)]">
            {row.primary_work_email}
          </div>
        </div>
      ),
    },
    {
      id: "title",
      header: t("tenant_detail.employees_col_title"),
      cell: (row) => row.job_title ?? "—",
    },
    {
      id: "department",
      header: t("tenant_detail.employees_col_department"),
      cell: (row) => row.department_name ?? "—",
    },
    {
      id: "state",
      header: t("tenant_detail.employees_col_state"),
      cell: (row) => <Badge tone={row.lifecycle_state === "active" ? "success" : "neutral"}>{row.lifecycle_state}</Badge>,
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div>
          <div className="nai-card-title">{t("tenant_detail.employees_title")}</div>
          <div className="nai-card-subtitle">{t("tenant_detail.employees_sub")}</div>
        </div>
      </CardHeader>
      <CardBody>
        {query.isLoading ? (
          <Loading label={t("tenant_detail.employees_loading")} rows={3} />
        ) : query.isError ? (
          <ErrorState
            title={resolveErrorDisplay(query.error, t).title}
            description={resolveErrorDisplay(query.error, t).description}
          />
        ) : (query.data?.employees?.length ?? 0) === 0 ? (
          <Empty
            title={t("tenant_detail.employees_empty")}
            description={t("tenant_detail.employees_empty_desc")}
          />
        ) : (
          <DataTable<TenantEmployee>
            columns={columns}
            rows={query.data?.employees ?? []}
            rowKey={(row) => row.employee_id}
            caption={t("tenant_detail.employees_title")}
          />
        )}
      </CardBody>
    </Card>
  );
}

const AUDIT_PAGE_SIZE = 25;

/**
 * Bu müşteriye karşı yapılmış operatör işlemleri. Her okuma backend'de bir
 * operator.audit_log_read satırı yazar; bu yüzden sekme açılmadan sorgu
 * kurulmaz (TabPanel etkin değilken alt ağacı bağlamaz).
 */
function TenantAuditPanel({ tenantId }: { readonly tenantId: string }): React.ReactElement {
  const { t } = useTranslation();
  const pager = useCursorStack();
  const query = useQuery({
    queryKey: ["operator", "audit", { tenant: tenantId, cursor: pager.cursor }],
    queryFn: () =>
      unwrap(
        operatorGraph.GET("/ops/v1/audit", {
          params: {
            query: {
              target_tenant_id: tenantId,
              limit: AUDIT_PAGE_SIZE,
              ...(pager.cursor !== null ? { cursor: pager.cursor } : {}),
            },
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });
  const events = query.data?.events ?? [];

  return (
    <Card>
      <CardHeader>
        <div>
          <div className="nai-card-title">{t("tenant_detail.audit_title")}</div>
          <div className="nai-card-subtitle">{t("tenant_detail.audit_sub")}</div>
        </div>
      </CardHeader>
      {query.isLoading ? (
        <Loading label={t("audit.loading")} rows={4} />
      ) : query.isError ? (
        <ErrorState
          title={t("audit.error_title")}
          description={resolveErrorDisplay(query.error, t).description}
        />
      ) : events.length === 0 ? (
        <Empty title={t("audit.empty_title")} description={t("tenant_detail.audit_empty_desc")} />
      ) : (
        <AuditEventsTable events={events} />
      )}
      <CursorPager
        stack={pager}
        shown={events.length}
        total={query.data?.total ?? 0}
        nextCursor={query.data?.next_cursor ?? null}
        busy={query.isLoading}
      />
    </Card>
  );
}

export function TenantDetailPage(): React.ReactElement {
  const { t } = useTranslation();
  return (
    <RequireAction
      action="tenants.read"
      loading={<Loading label={t("tenant_detail.loading")} rows={4} />}
      fallback={
        <ErrorState
          title={t("errors.classification.authorization.title")}
          description={t("errors.classification.authorization.fallback")}
        />
      }
    >
      <TenantDetailContent />
    </RequireAction>
  );
}

function TenantDetailContent(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const tenantId = id ?? "";
  const [startOpen, setStartOpen] = useState(false);
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [tab, setTab] = useState("overview");
  const canReadAudit = usePermission("audit.read");

  const query = useQuery({
    queryKey: ["operator", "tenant", tenantId],
    queryFn: () => fetchTenant(tenantId),
    enabled: tenantId !== "",
  });

  if (query.isLoading) {
    return <Loading label={t("tenant_detail.loading")} rows={4} />;
  }
  if (query.isError || query.data === undefined) {
    const display = resolveErrorDisplay(query.error, t);
    return <ErrorState title={t("tenant_detail.error_title")} description={display.description} />;
  }

  const { tenant } = query.data;
  const created = new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(
    new Date(tenant.created_at),
  );
  const modules = Object.entries(tenant.enabled_modules);
  // Hem alan listesi hem de sayfa spot'u ham enum basıyordu ("region_tr"),
  // üstelik spot'ta bir CÜMLENİN içinde. Komşu alanlar (plan, durum) zaten
  // çevriliyordu — aynı sızıntının listedeki ve cümledeki iki kopyası.
  const residencyLabel = t(`tenants.residency.${tenant.data_residency}`);

  const fields: ReadonlyArray<readonly [string, React.ReactNode]> = [
    ["plan", t(`tenants.plan.${tenant.plan_tier}`)],
    [
      "state",
      <Badge key="state" tone={STATE_TONE[tenant.state] ?? "neutral"}>
        {t(`tenants.state.${tenant.state}`)}
      </Badge>,
    ],
    ["residency", residencyLabel],
    ["locale", tenant.default_locale],
    ["timezone", tenant.default_time_zone],
    ["created", created],
  ];

  const overviewSection = (
    <div className="space-y-6">
        <div className="grid items-start gap-6 lg:grid-cols-[1.5fr_1fr]">
          {/* Modüller — mockup Modules grid */}
          <Card>
            <CardHeader>
              <div>
                <div className="nai-card-title">{t("tenant_detail.modules_title")}</div>
                <div className="nai-card-subtitle">{t("tenant_detail.modules_sub")}</div>
              </div>
            </CardHeader>
            <CardBody>
              {modules.length === 0 ? (
                // Açıklama LİSTE ekranından ödünç alınmıştı ("Bu filtreyle
                // eşleşen müşteri yok") — detay sayfasındaki modül kartında
                // ne filtre var ne de müşteri araması; okuyana yanlış bir
                // sebep söylüyordu.
                <Empty
                  title={t("tenant_detail.modules_empty")}
                  description={t("tenant_detail.modules_empty_desc")}
                />
              ) : (
                <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
                  {modules.map(([moduleId, enabled]) => (
                    <div
                      key={moduleId}
                      className={cn(
                        "rounded-[8px] border px-3 py-2.5",
                        enabled
                          ? "border-[color-mix(in_srgb,var(--accent)_30%,var(--border-subtle))] bg-[var(--accent-subtle-bg)]"
                          : "border-[var(--border-subtle)] bg-[var(--bg-subtle)]",
                      )}
                    >
                      {/* Modül adı bir ürün adıdır, tanımlayıcı değil: kart
                          "hris", "time", "leave" diye yedi ham anahtar
                          gösteriyordu. `enabled_modules` spec'te açık uçlu JSON
                          (kapalı enum DEĞİL), bu yüzden bilinmeyen anahtar
                          çeviri YOLUNU basmasın diye ham anahtara düşülüyor. */}
                      <div className="truncate font-sans text-[13px] font-medium text-[var(--fg-primary)]">
                        {t(`tenant_detail.module.${moduleId}`, { defaultValue: moduleId })}
                      </div>
                      <div
                        className={cn(
                          "mt-0.5 font-sans text-[11px] font-medium",
                          enabled ? "text-[var(--accent)]" : "text-[var(--fg-muted)]",
                        )}
                      >
                        {enabled ? t("tenant_detail.module_on") : t("tenant_detail.module_off")}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardBody>
          </Card>

          {/* Kimliğe bürünme kuralları — başlık aksiyonundaki butonun
              yanında duran kural kartı (cloud-only, gerekçe, oto-sonlanma). */}
          <Card className="border-[color-mix(in_srgb,var(--state-warning)_30%,var(--border-subtle))] bg-[color-mix(in_srgb,var(--state-warning)_8%,var(--bg-raised))]">
            <CardBody>
              <div className="flex gap-3">
                <UserCog
                  size={18}
                  aria-hidden="true"
                  className="mt-0.5 shrink-0 text-[var(--state-warning)]"
                />
                <div>
                  <div className="font-serif text-[14.5px] font-medium text-[var(--fg-primary)]">
                    {t("tenant_detail.impersonation_title")}
                  </div>
                  <p className="mb-0 mt-1 font-serif text-[13px] leading-relaxed text-[var(--fg-secondary)]">
                    {t("tenant_detail.impersonation_body")}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        <RequireAction action="tenants.employees.read" fallback={null} loading={null}>
          <TenantEmployeesCard tenantId={tenantId} />
        </RequireAction>
    </div>
  );

  return (
    <div className="mx-auto max-w-[1320px]">
      <RouterLink
        to="/tenants"
        className="mb-3 inline-flex items-center gap-1.5 font-sans text-[13px] font-medium text-[var(--accent)] no-underline hover:underline"
      >
        <ArrowLeft size={14} aria-hidden="true" />
        {t("tenant_detail.back")}
      </RouterLink>

      <PageHeader
        title={tenant.company_name}
        description={t("tenant_detail.lede", {
          residency: residencyLabel,
          country: tenant.primary_country,
          date: created,
        })}
        actions={
          <>
            <RequireAction action="tenants.provision">
              <Button variant="secondary" onClick={(): void => setProvisionOpen(true)}>
                <ServerCog size={14} aria-hidden="true" />
                {t("provision.button")}
              </Button>
            </RequireAction>
            {/* Kimliğe bürünme cloud-only (ADR-004): on-prem tenant'larda
                backend 409 operator.cloud_only_denied döner — bu yüzden
                butonu on_prem residency'de hiç çizmiyoruz (dürüst UX). */}
            {tenant.data_residency !== "on_prem" && (
              <RequireAction action="impersonate.start">
                <Button variant="primary" onClick={(): void => setStartOpen(true)}>
                  <UserCog size={14} aria-hidden="true" />
                  {t("tenant_detail.impersonate_button")}
                </Button>
              </RequireAction>
            )}
          </>
        }
      />

      {/* Kimlik şeridi — mockup header strip */}
      <Card className="mb-6">
        <CardBody>
          <div className="flex flex-wrap items-center gap-6">
            <TenantMark name={tenant.company_name} size={56} />
            <div className="grid flex-1 grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3 xl:grid-cols-6">
              {fields.map(([key, value]) => (
                <div key={key}>
                  <div className="font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--fg-muted)]">
                    {t(`tenant_detail.field.${key}`)}
                  </div>
                  <div className="mt-1 font-serif text-[16px] font-medium text-[var(--fg-primary)]">
                    {value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </CardBody>
      </Card>

      {canReadAudit ? (
        <Tabs value={tab} onValueChange={setTab}>
          <TabList aria-label={t("tenant_detail.tabs_label")}>
            <Tab value="overview">{t("tenant_detail.tab.overview")}</Tab>
            <Tab value="audit">{t("tenant_detail.tab.audit")}</Tab>
          </TabList>
          <TabPanel value="overview" className="pt-5">
            {overviewSection}
          </TabPanel>
          <TabPanel value="audit" className="pt-5">
            <TenantAuditPanel tenantId={tenant.tenant_id} />
          </TabPanel>
        </Tabs>
      ) : (
        overviewSection
      )}

      {startOpen && (
        <StartImpersonationModal
          onClose={(): void => setStartOpen(false)}
          lockedTenant={{ id: tenant.tenant_id, name: tenant.company_name }}
        />
      )}
      {provisionOpen && (
        <ProvisionTenantModal
          tenant={{ id: tenant.tenant_id, name: tenant.company_name }}
          onClose={(): void => setProvisionOpen(false)}
        />
      )}
    </div>
  );
}
